/**
 * Purpose: Modal to pick a society, scoped to a user-selected city. Both the
 *          city list and society list are searchable (long lists on mobile).
 *
 * Inputs:
 *   - open (boolean): whether the modal is visible.
 *   - onClose (fn): called when the user dismisses the modal.
 *   - title (string): modal heading (defaults to "Select your society").
 *   - onConfirm (fn<society>): called with the picked society object.
 *   - saving (boolean): disables controls while a parent save is in-flight.
 *   - requesterEmail (string): optional email for society area suggestion API (registration parity).
 *
 * Outputs: Rendered modal JSX.
 *
 * Behavior:
 *   - Step 1 (city): search field filters the known cities list; picking a city
 *     advances to step 2. If no city is picked, society list is hidden.
 *   - Step 2 (society): debounced remote search of societies for the chosen city.
 *     A "Change city" affordance returns to step 1 without dismissing the modal.
 *   - Escape closes the nested registration picker first, then this modal, unless `saving` is true.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { societyService } from '../../services/societyService';
import SocietyPickerModal from '../authentication/register/SocietyPickerModal';

const SEARCH_DEBOUNCE_MS = 300;
const CITY_LIST_MAX_HEIGHT = 'max-h-48';

const normalize = (s) => (s || '').toLowerCase().trim();

const SocietyCitySearchModal = ({
  open,
  onClose,
  title = 'Select your society',
  onConfirm,
  saving = false,
  requesterEmail = '',
}) => {
  const [cities, setCities] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);
  const [city, setCity] = useState('');

  const [societyQuery, setSocietyQuery] = useState('');
  const [societyList, setSocietyList] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(false);

  const [error, setError] = useState(null);
  const [suggestPickerOpen, setSuggestPickerOpen] = useState(false);
  const [suggestPickerMode, setSuggestPickerMode] = useState('pick');

  const loadCities = useCallback(async () => {
    setLoadingCities(true);
    setError(null);
    try {
      const response = await societyService.getCities();
      const raw = response.cities || [];
      setCities(Array.isArray(raw) ? [...raw].sort((a, b) => a.localeCompare(b)) : []);
    } catch (err) {
      setError('Failed to load cities.');
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setCity('');
    setCityQuery('');
    setSocietyQuery('');
    setSocietyList([]);
    setError(null);
    setSuggestPickerOpen(false);
    setSuggestPickerMode('pick');
    loadCities();
    return undefined;
  }, [open, loadCities]);

  useEffect(() => {
    if (!open || !city) {
      setSocietyList([]);
      return undefined;
    }
    setLoadingSocieties(true);
    setError(null);
    const t = setTimeout(() => {
      (async () => {
        try {
          const rows = await societyService.getSocietiesForCityAll(city, societyQuery, {
            forRegistration: false,
          });
          rows.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
          );
          setSocietyList(rows);
        } catch (e) {
          setError(e?.message || 'Could not load societies.');
          setSocietyList([]);
        } finally {
          setLoadingSocieties(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, city, societyQuery]);

  useEffect(() => {
    if (!open || suggestPickerOpen) return undefined;
    const onEscape = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open, suggestPickerOpen, onClose, saving]);

  const handleRegistrationPickerSelect = useCallback(
    async (societyId, options) => {
      setSuggestPickerOpen(false);
      setSuggestPickerMode('pick');
      let society = null;
      try {
        const data = await societyService.getSociety(societyId);
        if (data && data.id != null) society = { ...data };
      } catch (_) {
        /* fall through to minimal object */
      }
      if (!society) {
        society = {
          id: parseInt(societyId, 10),
          name: (options && options.name) || '',
          slug: (options && options.slug) || '',
          city,
        };
      }
      if (city && !society.city) society.city = city;
      await Promise.resolve(onConfirm(society));
    },
    [city, onConfirm],
  );

  const filteredCities = useMemo(() => {
    const q = normalize(cityQuery);
    if (!q) return cities;
    return cities.filter((c) => normalize(c).includes(q));
  }, [cities, cityQuery]);

  const handleCityPick = (next) => {
    setCity(next);
    setCityQuery('');
  };

  const handleCityChange = () => {
    setCity('');
    setSocietyQuery('');
    setSocietyList([]);
    setSuggestPickerOpen(false);
    setSuggestPickerMode('pick');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={
        saving
          ? undefined
          : () => {
              if (suggestPickerOpen) {
                setSuggestPickerOpen(false);
                setSuggestPickerMode('pick');
                return;
              }
              onClose();
            }
      }
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col border border-gray-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="society-city-search-title"
      >
        <div className="p-4 border-b border-gray-200">
          <h3 id="society-city-search-title" className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {city
              ? 'Now search or scroll to find your society.'
              : 'Pick your city first — type to filter the list.'}
          </p>

          {/* City block: searchable picker OR selected chip */}
          {!city ? (
            <div className="mt-3">
              <label htmlFor="society-city-search" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id="society-city-search"
                type="search"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                disabled={loadingCities || saving}
                placeholder={loadingCities ? 'Loading cities…' : 'Search city…'}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                autoComplete="off"
              />
              <div
                className={`mt-2 border border-gray-200 rounded-xl overflow-y-auto ${CITY_LIST_MAX_HEIGHT}`}
                role="listbox"
                aria-label="Cities"
              >
                {loadingCities && (
                  <p className="py-3 text-center text-gray-500 text-sm">Loading cities…</p>
                )}
                {!loadingCities && filteredCities.length === 0 && (
                  <p className="py-3 text-center text-gray-500 text-sm">
                    {cityQuery.trim() ? 'No cities match your search.' : 'No cities available.'}
                  </p>
                )}
                {!loadingCities && filteredCities.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                    {filteredCities.map((c) => (
                      <li key={c}>
                        <button
                          type="button"
                          onClick={() => handleCityPick(c)}
                          disabled={saving}
                          className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none disabled:opacity-50"
                          role="option"
                          aria-selected="false"
                        >
                          {c}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <span className="block text-sm font-medium text-gray-700 mb-1">City</span>
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <span className="text-sm font-medium text-emerald-900 truncate">{city}</span>
                <button
                  type="button"
                  onClick={handleCityChange}
                  disabled={saving}
                  className="text-sm text-emerald-700 hover:text-emerald-900 font-medium disabled:opacity-50"
                >
                  Change
                </button>
              </div>

              <label htmlFor="society-search-input" className="block text-sm font-medium text-gray-700 mt-3 mb-1">
                Search societies
              </label>
              <input
                id="society-search-input"
                type="search"
                value={societyQuery}
                onChange={(e) => setSocietyQuery(e.target.value)}
                disabled={saving}
                placeholder="Type to filter…"
                className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                autoComplete="off"
              />
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setSuggestPickerMode('pick');
                    setSuggestPickerOpen(true);
                  }}
                  className="w-full sm:w-auto sm:flex-1 rounded-xl border border-dashed border-blue-400 bg-blue-50/80 px-3 py-2.5 text-left text-sm font-medium text-blue-800 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
                >
                  Society not listed? Search full list or suggest
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setSuggestPickerMode('add');
                    setSuggestPickerOpen(true);
                  }}
                  className="w-full sm:w-auto sm:flex-1 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-center text-sm font-medium text-blue-800 hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
                >
                  Add society
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Society results (only after city picked) */}
        {city && (
          <div className="flex-1 min-h-0 overflow-y-auto p-2">
            {error && (
              <p className="py-2 px-2 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {loadingSocieties && !error && (
              <p className="py-4 text-center text-gray-500 text-sm">Loading…</p>
            )}
            {!loadingSocieties && !error && societyList.length === 0 && (
              <p className="py-4 text-center text-gray-500 text-sm">
                {societyQuery.trim()
                  ? 'No societies match your search.'
                  : 'No societies found for this city.'}
              </p>
            )}
            {!loadingSocieties && !error && societyList.length > 0 && (
              <ul className="space-y-0.5">
                {societyList.map((society) => (
                  <li key={society.id}>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onConfirm(society)}
                      className="w-full px-3 py-2.5 text-left rounded-xl hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none text-gray-900 disabled:opacity-50"
                    >
                      <span className="font-medium">{society.name}</span>
                      {society.city ? (
                        <span className="block text-xs text-gray-500">{society.city}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Error when no city yet picked (e.g. failed to load cities) */}
        {!city && error && (
          <p className="px-4 py-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="p-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-full px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      <SocietyPickerModal
        stackedAboveParentModal
        open={suggestPickerOpen}
        onClose={() => {
          setSuggestPickerOpen(false);
          setSuggestPickerMode('pick');
        }}
        city={city}
        requesterEmail={requesterEmail}
        initialMode={suggestPickerMode}
        initialName={societyQuery.trim()}
        onSelect={handleRegistrationPickerSelect}
      />
    </div>
  );
};

export default SocietyCitySearchModal;
